import { useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import GenerateDocumentButton from '@/components/common/GenerateDocumentButton';
import LoadingPage from '@/components/common/LoadingPage';
import PlatformLink from '@/components/common/PlatformLink';
import PropertiesList from '@/components/common/PropertiesList';
import SubPageHeader from '@/components/common/SubPageHeader';
import { Button } from '@/components/shadcn/button';
import getInsurerPortalUrl from '@/features/crm/utils/anva/getInsurerPortalUrl';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import queryKeys from '@/queries/queryKeys';

import useSelectedParty from '../analysis/hooks/useSelectedParty';

import PolicyItem from './components/PoliciesSection/PolicyItem';
import usePolicyPackage from './queries/hooks/usePolicyPackage';
import { dueDateToReadable, formatPremiumWithInterval } from './utils';

export default function PolicyPackagesDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();

  const queryClient = useQueryClient();

  const party = useSelectedParty();

  const { openSendMail } = useEmailClient();

  const { data: policyPackage, isLoading } = usePolicyPackage(
    party?.id ?? '',
    id ?? '',
    { enabled: Boolean(party?.id) && Boolean(id) },
  );

  const externalPortalUrl = getInsurerPortalUrl({
    insurerName: policyPackage?.insurance_company,
    anvaProductRef: policyPackage?.insurance_policies.find(
      (p) => p.anva_product_ref,
    )?.anva_product_ref,
  });

  const handleSendEmailInsurerPortalUrlMissing = () => {
    const subject = t('POLICY_PORTAL_EMAIL_SUBJECT', {
      insurer:
        policyPackage?.insurance_company ?? t('PARTY_CHECKUP_UNKNOWN_INSURER'),
    });
    const body = t('POLICY_PORTAL_EMAIL_BODY', {
      insurer:
        policyPackage?.insurance_company || t('PARTY_CHECKUP_UNKNOWN_INSURER'),
    });

    openSendMail({
      to: ['support@wegroup.nl'],
      subject,
      body,
    });
  };

  const handleInvalidatePolicyPackage = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.policyPackages.byId(party?.id ?? '', id ?? ''),
    });

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <SubPageHeader>
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span>{policyPackage?.name ?? ''}</span>

            {policyPackage?.carrier_type && (
              <span className="bg-card rounded border px-1 py-0.5 text-xs text-nowrap">
                {t(`CARRIER_TYPE_${policyPackage.carrier_type}`)}
              </span>
            )}
          </div>

          <Button size="icon-sm" variant="ghost" asChild>
            <PlatformLink
              to={`/parties/${party?.id}/current-insurances`}
              target="_blank"
            >
              <ExternalLink />
            </PlatformLink>
          </Button>
        </div>
      </SubPageHeader>

      <main className="flex-1 overflow-y-auto">
        {isLoading && <LoadingPage />}

        {!isLoading && policyPackage && (
          <div className="mt-1 flex flex-col gap-3 p-2">
            <PropertiesList
              items={[
                {
                  label: t('POLICY_PACKAGE_NR'),
                  value: policyPackage.package_nr,
                  copy: true,
                },
                {
                  label: t('PREMIUM'),
                  value: formatPremiumWithInterval(
                    policyPackage.premium,
                    policyPackage.payment_interval,
                    t,
                  ),
                },
                {
                  label: t('INSURER'),
                  value: policyPackage.insurance_company,
                },
                {
                  label: t('MAIN_DUE_DATE'),
                  value: dueDateToReadable(policyPackage.due_date),
                },
              ]}
            />

            <PropertiesList
              columns={1}
              items={[
                {
                  label: t('POLICY_DOCUMENT'),
                  value: policyPackage.policy_package_document_link ? (
                    <GenerateDocumentButton
                      link={policyPackage.policy_package_document_link}
                      onGenerate={handleInvalidatePolicyPackage}
                      generateLabel={
                        <>
                          {t('GENERATE_DOCUMENT')}

                          <Download className="size-4" />
                        </>
                      }
                      className="flex items-center gap-1 hover:underline"
                      variant="outline"
                      size="sm"
                    >
                      {t('OPEN_DOCUMENT')}

                      <ExternalLink className="size-4" />
                    </GenerateDocumentButton>
                  ) : (
                    <div className="text-muted-foreground/50">
                      {t('DOCUMENT_UNAVAILABLE')}
                    </div>
                  ),
                },
                {
                  label: t('POLICY_PORTAL_TITLE'),
                  value: externalPortalUrl ? (
                    <Link
                      to={externalPortalUrl}
                      className="flex items-center gap-1 hover:underline"
                      target="_blank"
                    >
                      {t('POLICY_PORTAL_OPEN')}

                      <ExternalLink className="size-4" />
                    </Link>
                  ) : (
                    <div className="flex flex-col items-start text-xs leading-normal">
                      <span>{t('POLICY_PORTAL_UNKNOWN')}</span>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendEmailInsurerPortalUrlMissing}
                      >
                        {t('POLICY_PORTAL_PROVIDE_LINK')}
                      </Button>
                    </div>
                  ),
                },
              ]}
            />

            <div className="mt-4 flex flex-col gap-2">
              <p>{t('POLICIES_TITLE')}</p>

              <div className="flex flex-col gap-2">
                {policyPackage.insurance_policies.map((policy) => (
                  <PolicyItem key={policy.id} policy={policy} />
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
